import type { HelpContext } from './content/pipeline';

/**
 * Help for dialogs: the one table of dialog → help. Pages need no entry — a page's help is every
 * section whose `routes` match the current path. A dialog shows the sections of the route it belongs
 * to, with sections whose id or keywords contain `topic` first. The build fails when a context's
 * route is not in the router or no section lists it (vite.helpContent.ts).
 *
 * Pure data (no React): the build imports it.
 */
export const HELP_CONTEXTS = {
  'absorb-charge': { route: '/payments', topic: 'payments-tabs' },
  'add-charge': { route: '/shipments/:id', topic: 'order-payments' },
  'allocate-credit': { route: '/payments', topic: 'shipper-receipt' },
  'assign-driver': { route: '/shipments/:id', topic: 'assign-driver' },
  'backfill-history': { route: '/analytics', topic: 'analytics' },
  'bids-and-award': { route: '/shipments/:id', topic: 'bids-and-award' },
  'bulk-import': { route: '/shippers', topic: 'bulk-import' },
  'cancel-invoice': { route: '/shipments/:id', topic: 'shipper-invoice' },
  'cancel-load': { route: '/work', topic: 'bidding' },
  'cancel-payment': { route: '/payments', topic: 'approvals-and-limits' },
  'change-request': { route: '/users/:id', topic: 'kyc-review' },
  'choose-partner': { route: '/payments', topic: 'partner-payout' },
  'choose-shipper': { route: '/payments', topic: 'shipper-receipt' },
  'claim-and-ownership': { route: '/shipments/:id', topic: 'claim-and-ownership' },
  'document-review': { route: '/documents', topic: 'documents-review' },
  'edit-shipment': { route: '/shipments/:id', topic: 'edit-shipment' },
  'generate-invoice': { route: '/payments', topic: 'shipper-invoice' },
  'interests': { route: '/interests', topic: 'interests' },
  'kyc-review': { route: '/users/:id', topic: 'kyc-review' },
  'mark-paid': { route: '/payments', topic: 'partner-payout' },
  'moving-again': { route: '/stopped', topic: 'stopped-trucks' },
  'new-payout': { route: '/payments', topic: 'payout' },
  'new-shipment': { route: '/orders', topic: 'shipment' },
  'notification-history': { route: '/shipments/:id', topic: 'notification-history' },
  'order-prices': { route: '/shipments/:id', topic: 'order-prices' },
  'partner-bill': { route: '/shipments/:id', topic: 'order-payments' },
  'payment-details': { route: '/payments', topic: 'payment-details' },
  'reassign-shipment': { route: '/shipments/:id', topic: 'reassign' },
  'record-receipt': { route: '/payments', topic: 'shipper-receipt' },
  'reject-charge': { route: '/payments', topic: 'approvals-and-limits' },
  'reject-payout': { route: '/payments', topic: 'approvals-and-limits' },
  'release-shipment': { route: '/shipments/:id', topic: 'release' },
  'review-charge': { route: '/payments', topic: 'approvals-and-limits' },
  'service-lane': { route: '/service-lanes', topic: 'service-lanes' },
  'settings-payments': { route: '/settings', topic: 'settings-money' },
  'shipment-documents': { route: '/shipments/:id', topic: 'documents-review' },
  'sign-out': { route: '/login', topic: 'sign out' },
  'trip-problems': { route: '/shipments/:id', topic: 'trip-problems' },
  'trip-status': { route: '/shipments/:id', topic: 'trip-status' },
  'truck-stopped': { route: '/shipments/:id', topic: 'trip-problems' },
  'truck-type': { route: '/settings', topic: 'truck' },
  'user-drawer': { route: '/fleets', topic: 'users-and-kyc' },
  'user-form': { route: '/fleets', topic: 'add-users' },
  'verify-claim': { route: '/payments', topic: 'shipper-receipt' },
} as const satisfies Record<string, HelpContext>;

export type HelpContextId = keyof typeof HELP_CONTEXTS;
